import { forwardRef, useCallback, useMemo, useRef, useState } from "react";
import type { ChangeEvent, ComponentProps } from "react";
import { Input } from "@/components/ui/input";
import { filterTextVariables, type TextVariable } from "@/components/editor/textVariables";

type TextVariableInputProps = Omit<ComponentProps<typeof Input>, "value" | "onChange"> & {
  value: string | number | readonly string[] | undefined;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onValueChange?: (value: string) => void;
};

type MatchRange = { start: number; end: number; query: string } | null;

export const TextVariableInput = forwardRef<HTMLInputElement, TextVariableInputProps>(
  (
    {
      value,
      onValueChange,
      placeholder,
      className,
      onFocus,
      onBlur,
      onChange,
      type,
      disabled,
      ...rest
    },
    forwardedRef,
  ) => {
    const localRef = useRef<HTMLInputElement>(null);
    const [match, setMatch] = useState<MatchRange>(null);
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    // Merge the forwarded ref with the local ref used for focus/selection management
    const mergedRef = useCallback(
      (node: HTMLInputElement | null) => {
        (localRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
        if (typeof forwardedRef === "function") {
          forwardedRef(node);
        } else if (forwardedRef) {
          (forwardedRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
        }
      },
      [forwardedRef],
    );

    const suggestions = useMemo<TextVariable[]>(() => {
      if (!match) return [];
      return filterTextVariables(match.query);
    }, [match]);

    const valueAsString = typeof value === "string" ? value : String(value ?? "");

    const applyValue = (nextValue: string, event?: ChangeEvent<HTMLInputElement>) => {
      if (onValueChange) onValueChange(nextValue);
      if (onChange) {
        if (event) {
          onChange(event);
        } else {
          onChange({ target: { value: nextValue } } as ChangeEvent<HTMLInputElement>);
        }
      }
    };

    const updateMatchFromValue = (nextValue: string, caret: number | null) => {
      if (caret === null || caret === undefined) {
        setMatch(null);
        setOpen(false);
        return;
      }
      const before = nextValue.slice(0, caret);
      const lastBracket = before.lastIndexOf("[");
      if (lastBracket === -1) {
        setMatch(null);
        setOpen(false);
        return;
      }
      const query = before.slice(lastBracket + 1);
      if (query.includes("]")) {
        setMatch(null);
        setOpen(false);
        return;
      }
      setMatch({ start: lastBracket, end: caret, query });
      setOpen(true);
      setActiveIndex(0);
    };

    const handleSuggestionClick = (variable: TextVariable) => {
      if (!match) return;

      if (variable.isPartial) {
        // Table selected – insert "tablename." and keep autocomplete open
        const insertText = variable.insertValue ?? variable.label;
        const nextValue =
          valueAsString.slice(0, match.start + 1) + insertText + valueAsString.slice(match.end);
        applyValue(nextValue);
        const newCaret = match.start + 1 + insertText.length;
        setMatch({ start: match.start, end: newCaret, query: insertText });
        setActiveIndex(0);
        requestAnimationFrame(() => {
          const input = localRef.current;
          if (!input) return;
          input.focus();
          input.setSelectionRange(newCaret, newCaret);
        });
      } else {
        // Field selected – insert full variable and close
        const nextValue =
          valueAsString.slice(0, match.start) + variable.label + valueAsString.slice(match.end);
        applyValue(nextValue);
        setMatch(null);
        setOpen(false);
        requestAnimationFrame(() => {
          const input = localRef.current;
          if (!input) return;
          const caret = match.start + variable.label.length;
          input.focus();
          input.setSelectionRange(caret, caret);
        });
      }
    };

    return (
      <div className="relative">
        <Input
          ref={mergedRef}
          type={type}
          value={value}
          onFocus={() => {
            onFocus?.();
            updateMatchFromValue(valueAsString, localRef.current?.selectionStart ?? null);
          }}
          onBlur={() => {
            onBlur?.();
            setOpen(false);
          }}
          disabled={disabled}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setOpen(false);
              return;
            }
            if (open && suggestions.length > 0) {
              if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                e.preventDefault();
                const dir = e.key === "ArrowDown" ? 1 : -1;
                setActiveIndex((i) => (i + dir + suggestions.length) % suggestions.length);
                return;
              }
              if (e.key === "Enter") {
                e.preventDefault();
                handleSuggestionClick(suggestions[activeIndex]);
                return;
              }
            }
          }}
          onChange={(e) => {
            const nextValue = e.target.value;
            applyValue(nextValue, e);
            updateMatchFromValue(nextValue, e.target.selectionStart);
          }}
          placeholder={placeholder}
          className={className}
          {...rest}
        />

        {open && suggestions.length > 0 && (
          <div
            className="absolute z-50 mt-1 w-full rounded-md border border-gray-600 bg-gray-800 shadow-lg"
            onMouseDown={(e) => e.preventDefault()}
          >
            {suggestions.map((variable, index) => (
              <button
                key={variable.label}
                type="button"
                className={`flex w-full flex-col items-start gap-0.5 px-2 py-1.5 text-left text-xs ${
                  index === activeIndex
                    ? "bg-gray-700 text-white"
                    : "text-gray-200 hover:bg-gray-700"
                }`}
                onClick={() => handleSuggestionClick(variable)}
                onMouseEnter={() => setActiveIndex(index)}
                aria-selected={index === activeIndex}
              >
                <span className="font-mono text-gray-100">{variable.label}</span>
                <span className="text-[11px] text-gray-400">{variable.description}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  },
);

TextVariableInput.displayName = "TextVariableInput";
